import React, { useContext } from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import Divider from '../Divider';
import { DrawerContentContainer, SideDrawerContentContainer } from '../SideDrawer';
import Button, { ButtonVariant } from '../Button';
import Stack, { contentStackSpacing } from '../Stack';
import { IconName } from '../Icon';
import { useIsDisabledByParent } from '../IsDisabledProvider';
import { getIntl } from '../../../utils/intl';
import { useFormikContext } from 'formik';
import { useIsFocused } from '@react-navigation/native';
import { SideDrawerContainerContext } from '../SideDrawer/SideDrawerContainer';
import InfoIcon from '../InfoIcon';
import Text from '../Text';
import { SideDrawerContentKeyContext } from '../SideDrawer/MaybeRenderedSideDrawer';
import { PRScrollView } from '../../shared/Scroll';

const intl = getIntl('v2BaseComponents.SideDrawerFormContainer', {
    save: 'Save',
    cancel: 'Cancel',
    closeConfirm: 'Discard Changes?',
    closeConfirmMessage: 'You have unsaved changes. Are you sure you want to close the form?',
    closeConfirmCancel: "Don't Close",
    closeConfirmConfirm: 'Close',
});

export type SideDrawerButtonProps = {
    title: string;
    variant: ButtonVariant;
    onPress: () => void;
    isDisabled?: boolean;
    testID?: string;
    icon: IconName;
    shouldShow?: boolean;
};

type SideDrawerButtonsProps = {
    buttons: SideDrawerButtonProps[];
};

export const ConfirmDirtyStateFormCloseContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { contentKey } = useContext(SideDrawerContentKeyContext);
    const { closeDrawer, isRequestingCloseConfirmation, setShouldRequestCloseConfirmation } =
        useContext(SideDrawerContainerContext);

    const showStyle: StyleProp<ViewStyle> = {
        width: '100%',
        height: '100%',
    };

    const hideStyle: StyleProp<ViewStyle> = {
        width: 0,
        height: 0,
        display: 'none',
    };

    return (
        <>
            <View style={isRequestingCloseConfirmation ? showStyle : hideStyle}>
                <SideDrawerContentContainer>
                    <Stack vertical fullWidth fullHeight align="center" justify="center" space={8}>
                        <InfoIcon
                            alertIconProps={{
                                name: 'AlertTriangle',
                                variant: 'warning',
                            }}
                            title={intl('closeConfirm')}
                        />
                        <Text variant="body-1">{intl('closeConfirmMessage')}</Text>
                        <Stack vertical fullWidth space={contentStackSpacing} align="center">
                            <Button
                                title={intl('closeConfirmConfirm')}
                                variant="primary"
                                icon="Check"
                                onPress={() => {
                                    closeDrawer(contentKey);
                                }}
                                size="lg"
                                testID="close-side-drawer-form-confirm"
                            />
                            <Button
                                title={intl('cancel')}
                                variant="secondary"
                                icon={'XCircle'}
                                onPress={() => setShouldRequestCloseConfirmation(false)}
                                size="lg"
                                testID="close-side-drawer-form-cancel"
                            />
                        </Stack>
                    </Stack>
                </SideDrawerContentContainer>
            </View>
            <View style={isRequestingCloseConfirmation ? hideStyle : showStyle}>{children}</View>
        </>
    );
};

const SideDrawerButtons: React.FC<SideDrawerButtonsProps> = ({ buttons }) => {
    return (
        <Stack horizontal fullWidth space={contentStackSpacing}>
            {buttons
                .filter((_) => _.shouldShow !== false)
                .map((button, index) => (
                    <Button
                        key={`${button.title}-${index}`}
                        variant={button.variant}
                        onPress={button.onPress}
                        isDisabled={button.isDisabled}
                        title={button.title}
                        style={{ flex: 1 }}
                        testID={button.testID}
                        icon={button.icon}
                    />
                ))}
        </Stack>
    );
};

export type SideDrawerFormContainerOwnProps = {
    children: React.ReactNode;
    isDisabled: boolean;
    onSubmit: () => void;
    onCancel: () => void;
    saveText?: string;
    cancelText?: string;
    saveIcon?: IconName;
    isSubmitDisabled?: boolean;
};

export type SideDrawerFormContainerProps = SideDrawerFormContainerOwnProps;

/**
 * Component to be placed inside of a side drawer form.  If the form is dirty, it will
 * notify the parent container to request confirmation before closing the drawer.
 */
export const SideDrawerDirtyFormExitValidationTracker: React.FC = () => {
    const formikContext = useFormikContext();
    const isFocused = useIsFocused();
    const { setShouldRequestCloseConfirmation } = useContext(SideDrawerContainerContext);

    React.useEffect(() => {
        if (!formikContext) return;

        const isDirty = formikContext.dirty && isFocused && !formikContext.isSubmitting;

        setShouldRequestCloseConfirmation(isDirty);

        return () => {
            setShouldRequestCloseConfirmation(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formikContext.dirty, isFocused, formikContext.isSubmitting]);

    return null;
};

const SideDrawerFormContainer: React.FC<SideDrawerFormContainerProps> = ({
    children,
    isSubmitDisabled,
    isDisabled,
    onSubmit,
    onCancel,
    cancelText,
    saveText,
    saveIcon,
}) => {
    const isDisabledByParent = useIsDisabledByParent(isDisabled);

    return (
        <SideDrawerButtonContainer
            buttons={[
                {
                    title: cancelText || intl('cancel'),
                    variant: 'secondary',
                    onPress: onCancel,
                    isDisabled: isDisabledByParent,
                    testID: 'form-submit-cancel',
                    icon: 'XCircle',
                },
                {
                    title: saveText || intl('save'),
                    variant: 'primary',
                    onPress: onSubmit,
                    isDisabled: isDisabledByParent || isSubmitDisabled,
                    testID: 'form-submit-button',
                    icon: saveIcon || 'CheckCircle',
                },
            ]}
        >
            {children}
        </SideDrawerButtonContainer>
    );
};

export default React.memo(SideDrawerFormContainer);

export type SideDrawerButtonContainerProps = {
    children: React.ReactNode;
    buttons: SideDrawerButtonProps[];
    contentContainerStyle?: StyleProp<ViewStyle>;
    disableScroll?: boolean;
    disablePadding?: boolean;
};

export const SideDrawerButtonContainer: React.FC<SideDrawerButtonContainerProps> = ({
    children,
    buttons,
    contentContainerStyle,
    disableScroll,
    disablePadding,
}) => {
    const drawerContainer = (
        <DrawerContentContainer
            style={[{ flex: 1, width: '100%', padding: disablePadding ? 0 : undefined }, contentContainerStyle]}
        >
            {children}
        </DrawerContentContainer>
    );

    const renderButtonsContent = () => (
        <>
            <Divider horizontal />
            <DrawerContentContainer style={[{ width: '100%' }]}>
                <SideDrawerButtons buttons={buttons} />
            </DrawerContentContainer>
        </>
    );

    const hasButtons = buttons.some((_) => _.shouldShow !== false);

    return (
        <View style={{ flex: 1, width: '100%' }}>
            {disableScroll ? (
                drawerContainer
            ) : (
                <PRScrollView style={{ flex: 1, width: '100%' }} contentContainerStyle={{ flexGrow: 1, width: '100%' }}>
                    {drawerContainer}
                </PRScrollView>
            )}
            {hasButtons ? renderButtonsContent() : null}
        </View>
    );
};
